import React from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Button, Stack } from '@lyearn/ui';
import * as Hero from '@/modules/corporate/components/DetailPageComponents/DetailHero';
import { useQuizDetailsContext } from '@/modules/corporate/modules/quiz/views/QuizDetails/context/QuizDetailsContext';
import getModifiedAttempts from '@/modules/corporate/modules/quiz/views/QuizDetails/utils/getQuizModifiedAttempts';

import { quizReviewContainerStyles } from '../../../QuizPlayer/components/Body/styles';
import QuizPlayerReview from '../../../QuizPlayer/views/QuizPlayerReview';
import AttemptHistory from '../AttemptHistory';
import QuizHeader from '../QuizDetailsQuizPlayer/components/QuizPlayerHeader/components/QuizHeader';
import QuizPlayerDialog from '../QuizPlayerDialog';
import ScoreAndAttemptsInfo from '../ScoreAndAttemptsInfo';
import { QuizDetailsActionProps, QuizDetailsActions } from './types';
import { getQuizDetailsActions } from './utils';

function QuizDetailsAction(props: QuizDetailsActionProps) {
  const { t } = useTranslation();
  const { isEmbed, content, responsive, quizPerformance, parentEntityId, learnerId } = props;
  const { onShowQuiz, onRetakeQuizAndToggle, fetchingRetakeQuiz, isLocalizedContent } =
    useQuizDetailsContext();

  const [showAttemptHistory, { toggle }] = useToggle(false);
  const [showReviewScreen, { on, off }] = useToggle(false);
  const attemptsHistory = getModifiedAttempts(quizPerformance);
  const actions = getQuizDetailsActions(content, attemptsHistory, quizPerformance);

  const onClick = React.useMemo(
    () => (id: QuizDetailsActions) => {
      switch (id) {
        case QuizDetailsActions.StartQuiz:
        case QuizDetailsActions.ResumeQuiz:
          onShowQuiz();
          break;
        case QuizDetailsActions.ReattemptQuiz:
          onRetakeQuizAndToggle();
          break;
        case QuizDetailsActions.ViewResults:
          on();
          break;
        case QuizDetailsActions.AttemptsHistory:
          toggle();
          break;
      }
    },
    [on, onRetakeQuizAndToggle, onShowQuiz, toggle],
  );

  return (
    <>
      <ScoreAndAttemptsInfo content={content} quizPerformance={quizPerformance} />
      {!responsive && (
        <Stack className="mt-32" gapX={8}>
          {actions.map((action, index) => (
            <Button
              key={action.id}
              loading={action.id === QuizDetailsActions.ReattemptQuiz && fetchingRetakeQuiz}
              variant={index === 0 ? 'filled' : 'outlined'}
              onClick={() => onClick(action.id)}>
              {t(action.labelKey)}
            </Button>
          ))}
          <Hero.Bookmark />
        </Stack>
      )}
      {showAttemptHistory ? (
        <AttemptHistory
          content={content}
          isEmbed={isEmbed}
          isLocalizedContent={isLocalizedContent}
          learnerId={learnerId}
          parentEntityId={parentEntityId}
          quizPerformance={quizPerformance}
          onClose={toggle}
        />
      ) : null}
      {showReviewScreen ? (
        <QuizPlayerDialog>
          <QuizPlayerReview
            attemptId={attemptsHistory[0].attemptId}
            className={quizReviewContainerStyles(isEmbed)}
            contentId={content.id}
            isEmbed={isEmbed}
            parentEntityId={parentEntityId}
            renderHeader={(quiz) => (
              <QuizHeader
                // canRetakeQuiz={canRetakeQuiz}
                // isPreviewing={isPreviewing}
                label={quiz.name}
                quizId={quiz.id}
                quizPerformance={quizPerformance}
                onClose={off}
                // onRetakeQuiz={onRetakeQuiz}
              />
            )}
            userId={learnerId}
          />
        </QuizPlayerDialog>
      ) : null}
    </>
  );
}

export default QuizDetailsAction;
